import { regionName, type Lang } from "./i18n";

const DATE_LOCALE: Record<Lang, string> = {
  en: "en-GB",
  nl: "nl-NL",
  it: "it-IT",
  de: "de-DE",
  fr: "fr-FR",
  es: "es-ES",
};

/** "2026-07-26" is a machine date. Show it the way the reader writes one. */
export function shortDate(iso: string, lang: Lang): string {
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(DATE_LOCALE[lang], {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(d);
}

// Country and region names were the last untranslated strings in the location
// line: an Italian page read "Pachino, near Marzamemi, Sicily, Italy", with two
// English words and an English preposition inside an Italian sentence. Regions
// already have a translation table; countries only needed one.
const COUNTRY_KEYS: Record<string, string> = {
  Italy: "co_italy",
  Spain: "co_spain",
  Greece: "co_greece",
  Croatia: "co_croatia",
  France: "co_france",
  Portugal: "co_portugal",
  "United Arab Emirates": "co_uae",
  Netherlands: "co_netherlands",
  Mexico: "co_mexico",
  Indonesia: "co_indonesia",
};

export function countryName(country: string, t: (k: string) => string): string {
  const key = COUNTRY_KEYS[country];
  if (!key) return country;
  const translated = t(key);
  return translated === key ? country : translated;
}

/** "Pachino, near Marzamemi" -> "Pachino, vicino a Marzamemi" */
export function townName(town: string, t: (k: string) => string): string {
  return town.replace(/\bnear\b/i, t("near_word"));
}

export function locationLine(
  club: { town: string; region_key: string; region_label: string; country: string },
  t: (k: string) => string,
): string {
  return [
    townName(club.town, t),
    regionName(club.region_key, club.region_label, t),
    countryName(club.country, t),
  ]
    .filter(Boolean)
    .join(", ");
}

// A time range takes an en dash, not a hyphen. A hyphen joins ("cost-effective");
// an en dash spans ("08:30–22:00"). This runs before anything else and on every
// language, so a stored Italian translation gets it too rather than only the
// English source that happens to match the pattern below.
function enDashRanges(text: string): string {
  return text.replace(/(\d{1,2}[:.]\d{2})\s*-\s*(\d{1,2}[:.]\d{2})/g, "$1–$2");
}

// Opening hours were researched in English and printed under an Italian
// heading: "Orari di apertura" over "Daily 08:30-22:00". Roughly a third of the
// values follow one of three shapes, and those three are worth translating; the
// rest are free prose that only a real translation pass can fix, so they are
// left exactly as the source wrote them rather than half-converted.
export function openingHours(raw: string | null, t: (k: string) => string): string | null {
  if (!raw?.trim()) return null;
  const text = enDashRanges(raw.trim());

  const daily = text.match(/^daily\s+(\d{1,2}[:.]\d{2})\s*[-–]\s*(\d{1,2}[:.]\d{2})(.*)$/i);
  if (daily) {
    const inSeason = /in season/i.test(daily[3]);
    const rest = daily[3].replace(/,?\s*in season/i, "").trim();
    return [
      `${t("oh_daily")} ${daily[1].replace(".", ":")}–${daily[2].replace(".", ":")}`,
      inSeason ? t("oh_in_season") : "",
      rest,
    ]
      .filter(Boolean)
      .join(", ");
  }
  if (/^daily in season$/i.test(text)) return `${t("oh_daily")}, ${t("oh_in_season")}`;
  if (/^summer season$/i.test(text)) return t("oh_summer");
  return text;
}

// 147 of 827 clubs publish a rate, and every one of those describes what the
// rate buys in its own words: "2 beds + parasol", "per pair of sunbeds per
// day", "per umbrella with 2 deckchairs and 1 sunbed per day, rear area, max 4
// people". Dropping the unit would make the prices incomparable; printing it
// raw puts an English sentence next to an Italian price.
//
// So: fold the common shapes onto a short translated phrase, and keep anything
// that does not fit as a separate detail line rather than in the price itself.
// Order matters, the first match wins, most specific first.
const UNIT_PATTERNS: [RegExp, string][] = [
  [/day pass|beach pass|pool and beach|beach day access|entrance per (person|adult)/, "pu_daypass"],
  [/(2|two|pair of|set of two|双)\s*(sun ?beds?|loungers?|deck ?chairs?|beds?|mattress)/, "pu_two_beds"],
  [/umbrella (with|and).*(sunbed|lounger|deck ?chair)|parasol \+ 2|1 umbrella \+ 2/, "pu_two_beds"],
  [/(sun ?bed|lounger|deck ?chair|mattress|bed).*(parasol|umbrella)|(parasol|umbrella).*(sun ?bed|lounger|bed)/, "pu_bed_parasol"],
  [/per (umbrella|parasol)/, "pu_umbrella"],
  [/per (person|adult|guest)/, "pu_person"],
  [/per (spot|pitch|place|station)/, "pu_spot"],
  [/half day|per morning|from 1[45]:00|from 19:00/, "pu_half_day"],
  [/(sun ?bed|lounger|deck ?chair|bed|mattress|seabed)/, "pu_bed"],
];

const PER_DAY = /per day|\/day|per dag|day rate|day pass|\bdaily\b/i;
const PER_WEEK = /per week/i;

export type PriceUnit = {
  /** Short, translated, safe to sit next to the amount. Null when unclassifiable. */
  short: string | null;
  /** The club's own wording, when it says more than the short form does. */
  detail: string | null;
};

export function priceUnit(raw: string | null, t: (k: string) => string): PriceUnit {
  if (!raw?.trim()) return { short: null, detail: null };
  const text = raw.toLowerCase();

  let base: string | null = null;
  for (const [re, key] of UNIT_PATTERNS) {
    if (re.test(text)) {
      base = t(key);
      break;
    }
  }
  if (!base) return { short: null, detail: raw };

  const period = PER_WEEK.test(text) ? t("pu_per_week") : PER_DAY.test(text) ? t("pu_per_day") : null;
  const short = period ? `${base}, ${period}` : base;

  // A short unit answers "what do I get". Everything the club added beyond
  // that (rows, seasons, minimum spend, extras) is real information a traveler
  // wants, so it survives as a detail line rather than being thrown away.
  const detail = raw.length > 28 || /[(,;]/.test(raw) ? raw : null;
  return { short, detail };
}

